/**
 * Distribute widget configuration — pure (no React), so the editor, the
 * widget runtime, the hosted page and server routes can all share it.
 * `hooks/useEmbedEditor.ts` re-exports everything here alongside the hook.
 */

import {
  type AdvancedWidgetConfig,
  type AnimationConfig,
  type AvatarConfig,
  type BackgroundConfig,
  type BehaviorConfig,
  type BorderStyleConfig,
  type ContentConfig,
  DEFAULT_ADVANCED_CONFIG,
  type HeaderConfig,
  type InputConfig,
  type LauncherConfig,
  type LoadingIndicatorConfig,
  type MessageBubblesConfig,
  type MicroInteractionsConfig,
  type PositionConfig,
  type ScrollbarConfig,
  type SendButtonConfig,
  type SpacingConfig,
  type TimestampConfig,
  type TypographyConfig,
} from './theme/types';
import { sanitizeWidgetImage, type WidgetLayout } from './widgetAttributes';

// Re-export advanced types for consumers
export type {
  AdvancedWidgetConfig,
  BackgroundConfig,
  MessageBubblesConfig,
  PositionConfig,
  LauncherConfig,
  AnimationConfig,
  HeaderConfig,
  InputConfig,
  ContentConfig,
  BehaviorConfig,
  TypographyConfig,
  AvatarConfig,
  SpacingConfig,
  ScrollbarConfig,
  TimestampConfig,
  SendButtonConfig,
  MicroInteractionsConfig,
  BorderStyleConfig,
  LoadingIndicatorConfig,
};

// Re-export default advanced config
export { DEFAULT_ADVANCED_CONFIG };

// ============================================================================
// Types
// ============================================================================

export type EmbedType = 'brain' | 'profile';
export type ThemeMode = 'light' | 'dark';
export type BorderRadius = 'none' | 'sm' | 'md' | 'lg' | 'full';
export type EmbedMode = 'normal' | 'embed';
export type AgentIconStyle = 'robot' | 'sparkle' | 'brain' | 'chat' | 'message';

export interface ThemePreset {
  id: string;
  name: string;
  mode: ThemeMode;
  color: string;
}

export interface SelectedBrain {
  id: string;
  name: string;
  displayImage?: string;
  description?: string;
}

export type WidgetPosition = 'bottom-left' | 'bottom-right';

export type WidgetMobileMode = 'sheet' | 'fullscreen';

export type ChatStyle = 'bubbles' | 'minimal' | 'cards';

export interface EmbedConfig {
  type: EmbedType;
  themePreset: string;
  useCustomTheme: boolean;
  customMode: ThemeMode;
  primaryColor: string;
  borderRadius: BorderRadius;
  /** Floating: panel width (px). Empty = default. */
  width: string;
  /** Floating: panel height; inline / iframe: block height (px). Empty = default. */
  height: string;
  // Multi-select arrays
  selectedBrains: SelectedBrain[];
  // Legacy single-select (kept for backward compatibility)
  brainId: string;
  brainName: string;
  profileHandle: string;
  /** Embed mode: 'normal' = direct React component, 'embed' = shadow-sm DOM web component */
  embedMode: EmbedMode;
  /** Agent icon style for AI messages (and the chat button when buttonStyle is 'icon') */
  agentIcon: AgentIconStyle;
  /** Chat button: the widget's picture (uploaded image, else the brain's) or one of the icons */
  buttonStyle?: WidgetButtonStyle;
  /** Guest mode: when true, sign-in option is hidden (for external embeds where auth should be disabled) */
  guestMode: boolean;
  /** Widget position: 'bottom-left' or 'bottom-right' */
  position: WidgetPosition;
  /** Optional text label shown next to the floating button (turns the FAB into a pill) */
  floatingLabel: string;
  /** Optional greeting tooltip shown ~2s after load while the panel is closed */
  greeting: string;
  /** Mobile layout for the open panel: bottom sheet (default) or fullscreen */
  mobileMode: WidgetMobileMode;
  /** Custom conversation starters (pills) shown in the empty state; overrides the
   *  brain's launch prompts when non-empty */
  conversationStarters: string[];
  /** Chat bubble style: bubbles (default), minimal, or cards */
  chatStyle: ChatStyle;
  /** Header: override the brain's display name */
  displayNameOverride: string;
  /** Header: subtitle line shown under the name */
  headerSubtitle: string;
  /** Header: override the brain's display image (URL) */
  displayImageOverride: string;
  /** Where the widget sits on the page: floating launcher (default), inline section, or full page */
  layout?: WidgetLayout;
  /** Brand font id from theme/fonts.ts ('' / undefined keeps the widget default) */
  fontFamily?: string;
  /** Custom theme only: follow the visitor's OS light / dark setting instead of customMode */
  autoDarkMode?: boolean;
  /** Advanced widget configuration for fine-grained control */
  advanced: AdvancedWidgetConfig;
}

export type WidgetButtonStyle = 'picture' | 'icon';

export interface AgentIconOption {
  id: AgentIconStyle;
  name: string;
  description: string;
}

export interface Brain {
  _id: string;
  name: string;
  displayImage?: string;
  /** "Who can chat" setting — editor-only, never written into the install code */
  askAccessMode?: string;
}

// ============================================================================
// Constants
// ============================================================================

export const THEME_PRESETS: ThemePreset[] = [
  { id: 'default', name: 'Default', mode: 'light', color: '#3b82f6' },
  { id: 'dark', name: 'Dark', mode: 'dark', color: '#3b82f6' },
  { id: 'modern', name: 'Modern', mode: 'light', color: '#8b5cf6' },
  { id: 'minimal', name: 'Minimal', mode: 'light', color: '#6b7280' },
  { id: 'bold', name: 'Bold', mode: 'light', color: '#f97316' },
  { id: 'ocean', name: 'Ocean', mode: 'light', color: '#0891b2' },
  { id: 'forest', name: 'Forest', mode: 'light', color: '#059669' },
  { id: 'rose', name: 'Rose', mode: 'light', color: '#e11d48' },
  { id: 'midnight', name: 'Midnight', mode: 'dark', color: '#6366f1' },
  { id: 'ember', name: 'Ember', mode: 'dark', color: '#f97316' },
];

export const AGENT_ICON_OPTIONS: AgentIconOption[] = [
  { id: 'robot', name: 'Robot', description: 'Classic robot face' },
  { id: 'sparkle', name: 'Sparkle', description: 'AI sparkle/magic' },
  { id: 'brain', name: 'Brain', description: 'Brain icon' },
  { id: 'chat', name: 'Chat', description: 'Chat bubble' },
  { id: 'message', name: 'Message', description: 'Message icon' },
];

export const DEFAULT_CONFIG: EmbedConfig = {
  type: 'profile',
  themePreset: 'default',
  useCustomTheme: false,
  customMode: 'light',
  primaryColor: '#3b82f6',
  borderRadius: 'lg',
  width: '', // Empty means 100%
  height: '', // Empty means 100%
  // Multi-select arrays
  selectedBrains: [],
  // Legacy single-select
  brainId: '',
  brainName: '',
  profileHandle: '',
  embedMode: 'embed', // Default to embed mode (shadow-sm DOM) for style isolation
  agentIcon: 'message', // Default agent icon
  buttonStyle: 'picture', // The widget's picture on the chat button (an icon when there is none)
  guestMode: false, // Default to false to show sign-in option
  position: 'bottom-right', // Default widget position
  floatingLabel: '', // No label by default (icon-only FAB)
  greeting: '', // No greeting tooltip by default
  mobileMode: 'sheet', // Default mobile layout
  conversationStarters: [], // No custom starters by default (uses the brain's launch prompts)
  chatStyle: 'bubbles', // Default chat bubble style
  displayNameOverride: '', // Use the brain's name unless overridden
  headerSubtitle: '', // No header subtitle by default
  displayImageOverride: '', // Use the brain's image unless overridden
  layout: 'floating',
  fontFamily: '',
  autoDarkMode: false,
  advanced: DEFAULT_ADVANCED_CONFIG,
};

// ============================================================================
// Security Helpers
// ============================================================================

/**
 * Escapes a string for safe inclusion in JavaScript string literals.
 * Prevents XSS attacks via template injection.
 */
export function escapeJSString(str: string): string {
  if (!str) return '';
  return str
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t')
    .replace(/</g, '\\x3c')
    .replace(/>/g, '\\x3e');
}

/**
 * Validates and sanitizes a hex color value.
 * Returns default color if invalid.
 */
export function sanitizeColor(color: string): string {
  const hexRegex = /^#[0-9A-Fa-f]{6}$/;
  return hexRegex.test(color) ? color : '#3b82f6';
}

/**
 * Validates and sanitizes a dimension value.
 * Returns clamped value within min/max bounds.
 */
export function sanitizeDimension(value: string, min: number, max: number, defaultVal: number): number {
  const num = parseInt(value, 10);
  if (isNaN(num)) return defaultVal;
  return Math.max(min, Math.min(max, num));
}

/**
 * Validates an alphanumeric ID (MongoDB ObjectId format).
 */
export function isValidId(id: string): boolean {
  return /^[a-zA-Z0-9-]+$/.test(id);
}

/**
 * Validates a handle (alphanumeric, dots, underscores, hyphens).
 */
export function isValidHandle(handle: string): boolean {
  return /^[a-zA-Z0-9._-]+$/.test(handle);
}

// ============================================================================
// Embed Code Generator
// ============================================================================

/**
 * Helper to escape JSON for HTML attributes
 */
function escapeJsonForAttribute(json: string): string {
  return json.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Check if advanced config differs from defaults
 */
function hasAdvancedConfigChanges(config: AdvancedWidgetConfig): boolean {
  return JSON.stringify(config) !== JSON.stringify(DEFAULT_ADVANCED_CONFIG);
}

export interface WidgetInstallContext {
  userId: string;
  userHandle: string;
  appBaseUrl: string;
  apiBaseUrl?: string;
  mediaBaseUrl?: string;
  assetApiUrl?: string;
}

/** Inline layout height (px) when the user hasn't set one. */
export const DEFAULT_INLINE_HEIGHT = 640;

/**
 * The <bolder-widget> attributes for a config, in snippet order. This is the
 * single source for the script snippet, the hosted page link (and so the
 * iframe), and the Distribute live preview — what you preview is what you paste.
 * Values are raw here; each output format escapes them for its own context.
 */
export function buildWidgetAttributes(
  config: EmbedConfig,
  ctx: WidgetInstallContext,
): Array<[string, string]> {
  const attrs: Array<[string, string]> = [];
  const push = (name: string, value: string | undefined | null) => {
    if (value !== undefined && value !== null && value !== '') attrs.push([name, value]);
  };

  // Theme: a preset, or the brand colour + light / dark / auto (follows the visitor's OS).
  if (config.useCustomTheme) {
    push('theme', config.autoDarkMode ? 'auto' : config.customMode === 'dark' ? 'dark' : 'light');
    push('primary-color', sanitizeColor(config.primaryColor));
    push(
      'border-radius',
      ['none', 'sm', 'md', 'lg', 'full'].includes(config.borderRadius) ? config.borderRadius : 'lg',
    );
  } else {
    push('theme', isValidHandle(config.themePreset || '') ? config.themePreset : 'default');
  }
  if (config.fontFamily && isValidHandle(config.fontFamily)) push('font-family', config.fontFamily);

  const layout: WidgetLayout = config.layout ?? 'floating';
  if (layout !== 'floating') push('layout', layout);
  // Inline: how tall the chat is on the page (the runtime sizes its host
  // element with it when the site hasn't).
  if (layout === 'inline') {
    push(
      'embed-height',
      String(
        config.height
          ? sanitizeDimension(config.height, 420, 1200, DEFAULT_INLINE_HEIGHT)
          : DEFAULT_INLINE_HEIGHT,
      ),
    );
  }

  push('owner-id', isValidId(ctx.userId) ? ctx.userId : '');
  push('owner-handle', isValidHandle(ctx.userHandle) ? ctx.userHandle : '');
  push('app-base-url', ctx.appBaseUrl);
  push('api-base-url', ctx.apiBaseUrl);
  push('media-base-url', ctx.mediaBaseUrl);
  push('asset-api-url', ctx.assetApiUrl);

  const agentIcon = AGENT_ICON_OPTIONS.some((o) => o.id === config.agentIcon) ? config.agentIcon : 'message';
  if (agentIcon !== 'message') push('agent-icon', agentIcon);
  if (config.guestMode) push('guest-mode', 'true');

  // Launcher + panel only matter for the floating layout.
  if (layout === 'floating') {
    if (config.buttonStyle === 'icon') push('button-style', 'icon');
    if (config.position === 'bottom-left') push('position', 'bottom-left');
    if (config.width) push('panel-width', String(sanitizeDimension(config.width, 320, 600, 380)));
    if (config.height) push('panel-height', String(sanitizeDimension(config.height, 420, 900, 600)));
    push('floating-label', config.floatingLabel?.trim());
    push('greeting', config.greeting?.trim());
    if (config.mobileMode === 'fullscreen') push('mobile-mode', 'fullscreen');
  }

  const starters = (config.conversationStarters || []).map((s) => s.trim()).filter(Boolean);
  if (starters.length > 0) push('conversation-starters', JSON.stringify(starters));
  if (config.chatStyle && config.chatStyle !== 'bubbles') push('chat-style', config.chatStyle);
  push('display-name-override', config.displayNameOverride?.trim());
  push('header-subtitle', config.headerSubtitle?.trim());
  push('display-image-override', sanitizeWidgetImage(config.displayImageOverride));
  if (config.selectedBrains && config.selectedBrains.length > 0) {
    push('selected-brains', JSON.stringify(config.selectedBrains));
  }
  if (config.advanced && hasAdvancedConfigChanges(config.advanced)) {
    push('advanced-config', JSON.stringify(config.advanced));
  }
  return attrs;
}

/** Host-element style for the snippet: none for floating (the widget overlays the page). */
function widgetElementStyle(config: EmbedConfig): string {
  const layout = config.layout ?? 'floating';
  if (layout === 'inline') {
    const height = config.height
      ? sanitizeDimension(config.height, 420, 1200, DEFAULT_INLINE_HEIGHT)
      : DEFAULT_INLINE_HEIGHT;
    return `display: block; width: 100%; height: ${height}px;`;
  }
  if (layout === 'fullpage') return 'display: block; width: 100%; height: 100vh;';
  return '';
}

/** Script + <bolder-widget> snippet (works on any site that accepts custom HTML). */
export function generateEmbedCode(
  config: EmbedConfig,
  _embedPath: string,
  userId: string,
  userHandle: string,
  appBaseUrl: string,
  apiBaseUrl: string = '',
  mediaBaseUrl: string = '',
  assetApiUrl: string = '',
): string {
  const attrs = buildWidgetAttributes(config, {
    userId,
    userHandle,
    appBaseUrl,
    apiBaseUrl,
    mediaBaseUrl,
    assetApiUrl,
  });
  const style = widgetElementStyle(config);
  const lines = attrs.map(([name, value]) => `  ${name}="${escapeJsonForAttribute(value)}"`);
  if (style) lines.push(`  style="${style}"`);
  return `<!-- Bolder AI assistant -->
<script src="${escapeJsonForAttribute(appBaseUrl)}/embed/bolder-widget.js" async></script>
<bolder-widget
${lines.join('\n')}>
</bolder-widget>`;
}

// ============================================================================
// Saved-widget install code (Distribute)
// ============================================================================

const WIDGET_ID = /^[a-f0-9]{24}$/i;

function trimBase(appBaseUrl: string): string {
  return appBaseUrl.replace(/\/$/, '');
}

/** The two lines a site pastes once. Everything else is read by id at load time. */
export function widgetInstallCode(widgetId: string, appBaseUrl: string): string {
  if (!WIDGET_ID.test(widgetId)) return '';
  const base = escapeJsonForAttribute(trimBase(appBaseUrl));
  return `<script src="${base}/embed/bolder-widget.js" async></script>
<bolder-widget widget-id="${widgetId}"></bolder-widget>`;
}

/** The widget's own full page — the shareable link and the iframe target. */
export function widgetHostedUrl(widgetId: string, appBaseUrl: string): string {
  if (!WIDGET_ID.test(widgetId)) return '';
  return `${trimBase(appBaseUrl)}/assistant/${widgetId}`;
}

/** <iframe> of the hosted page, for builders that only accept iframes. */
export function widgetIframeCode(widgetId: string, appBaseUrl: string, config: EmbedConfig): string {
  const url = widgetHostedUrl(widgetId, appBaseUrl);
  if (!url) return '';
  const height = config.height
    ? sanitizeDimension(config.height, 420, 1200, DEFAULT_INLINE_HEIGHT)
    : DEFAULT_INLINE_HEIGHT;
  const title = config.displayNameOverride?.trim() || config.selectedBrains?.[0]?.name || 'AI assistant';
  return `<iframe
  src="${escapeJsonForAttribute(url)}"
  title="${escapeJsonForAttribute(title)}"
  style="width: 100%; height: ${height}px; border: 0; border-radius: 16px;"
  allow="clipboard-write"
  loading="lazy">
</iframe>`;
}
